import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSupportRealtime } from '../hooks/useSupportRealtime';
import { useToast } from './ToastProvider';
import { maybeInAppSupportNotify, registerSupportServiceWorker } from '../utils/supportPush';
import {
  parseSupportOpenMessage,
  requestOpenSupportTicket,
} from '../utils/supportOpen';

/**
 * Global support alerts (WS + in-app notify) while any admin page is open —
 * not only when SupportPage is mounted.
 */
export function SupportNotifyBridge() {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const onSupportPage = location.pathname.replace(/\/$/, '').endsWith('/support');

  useEffect(() => {
    void registerSupportServiceWorker(`${import.meta.env.BASE_URL}sw.js`);
  }, []);

  // Service worker push click → jump to ticket (works from any admin page).
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (event: MessageEvent) => {
      const parsed = parseSupportOpenMessage(event.data);
      if (!parsed) return;
      const onSupport = window.location.pathname.replace(/\/$/, '').endsWith('/support');
      navigate(`/support?ticket=${parsed.ticketId}`);
      // If Support is already open, refresh immediately; otherwise wait for mount.
      window.setTimeout(
        () => requestOpenSupportTicket(parsed.ticketId, true),
        onSupport ? 0 : 120,
      );
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  const { sendReceipt } = useSupportRealtime(
    (event) => {
      if (event.type !== 'ticket_snapshot' || !event.ticket) return;
      // Delivered as soon as peer's client sees the message (any admin page).
      if (event.ticket.last_message_author_role === 'user' && event.ticket.last_message_id) {
        sendReceipt(event.ticket_id, 'delivered', event.ticket.last_message_id);
      }
      if (event.ticket.last_message_author_role !== 'user' || !event.ticket.last_message_id) return;

      const preview = event.ticket.last_message_preview || 'Новое сообщение';
      const ticketId = event.ticket_id;
      const go = () => {
        navigate(`/support?ticket=${ticketId}`);
        // SupportPage listens for this when already mounted; delay covers first mount.
        window.setTimeout(() => requestOpenSupportTicket(ticketId, true), 80);
      };
      maybeInAppSupportNotify({
        title: 'Тикет поддержки',
        body: `${event.ticket.subject}: ${preview}`,
        ticketId,
        selectedTicketId: null,
        dedupeKey: `admin-global-${event.ticket.last_message_id}`,
        onToast: (msg) => toast.notify(msg, { onClick: go }),
        onClick: go,
      });
    },
    { enabled: !onSupportPage },
  );

  return null;
}
